import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCheck, Clock } from "lucide-react";
import type { HandshakeMessageFull } from "@/types";
import { cn } from "@/lib/utils";
import {
  formatFullDate,
  handshakeMessageSenderConfig,
  handshakeMessageSenderLabel,
} from "./config";
import { MessageStatusBadge } from "./HandshakeBadges";

interface HandshakeAuditMessageBubbleProps {
  message: HandshakeMessageFull;
  className?: string;
}

function isInternalChannel(channel: string | null | undefined): boolean {
  return channel === "INTERNAL" || channel === "PORTAL";
}

export function HandshakeAuditMessageBubble({
  message,
  className,
}: HandshakeAuditMessageBubbleProps) {
  const { t } = useTranslation("handshake");
  const senderConfig = handshakeMessageSenderConfig(message);
  const SenderIcon = senderConfig.icon;
  const isBlocked = message.message_status === "BLOCKED";
  const delivered = message.delivery_status === "DELIVERED";
  const internal = isInternalChannel(message.channel);
  const hasGpt =
    message.gpt_analysis && Object.keys(message.gpt_analysis).length > 0;

  const deliveryLabel = internal
    ? t("audit.internalNote")
    : delivered
      ? t("audit.sent")
      : t("audit.notSent");

  return (
    <article
      className={cn(
        "rounded-lg px-3 py-2.5",
        isBlocked
          ? "border border-error-200 bg-error-50/50"
          : "border border-surface-200/70 bg-surface-50/80 dark:bg-surface-100/40",
        className,
      )}
    >
      <header className="mb-1.5 flex flex-wrap items-center gap-2">
        <div
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-md",
            senderConfig.tileClass,
          )}
        >
          {SenderIcon ? (
            <SenderIcon className="h-3 w-3" />
          ) : null}
        </div>
        <span className={cn("text-sm font-semibold", senderConfig.color)}>
          {handshakeMessageSenderLabel(message)}
        </span>
        {isBlocked ? (
          <MessageStatusBadge status={message.message_status} />
        ) : null}
        {message.agreement_detected ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success-700">
            <CheckCheck className="h-3 w-3" />
            {t("bubble.agreementDetected")}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1 text-xs tabular-nums text-surface-400">
          <Clock className="h-3 w-3" />
          {formatFullDate(message.created_at)}
        </span>
      </header>

      <p
        className={cn(
          "whitespace-pre-wrap text-sm leading-relaxed text-surface-800 [overflow-wrap:anywhere]",
          isBlocked && "text-error-900",
        )}
      >
        {message.content}
      </p>

      {isBlocked && message.block_reason ? (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-surface-0/80 p-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-error-500" />
          <p className="text-xs text-error-700">
            <span className="font-medium">{t("audit.blockReason")}: </span>
            {message.block_reason}
          </p>
        </div>
      ) : null}

      <footer className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-surface-400">
        <span>
          {deliveryLabel}
          {!internal && message.channel
            ? ` · ${t("audit.viaChannel", { channel: message.channel })}`
            : null}
        </span>
        {hasGpt ? (
          <details>
            <summary className="cursor-pointer hover:text-surface-600">
              {t("audit.gptAnalysis")}
            </summary>
            <pre className="mt-1 max-h-36 overflow-auto rounded-md bg-surface-0 p-2 text-[11px] text-surface-600">
              {JSON.stringify(message.gpt_analysis, null, 2)}
            </pre>
          </details>
        ) : null}
      </footer>
    </article>
  );
}
